import { useState, useEffect } from "react";
import { ArrowLeft, HelpCircle, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { settingsApi } from "@/api/settingsApi";
import { useToastHandler } from "@/hooks/useToastHandler";
import PageTitleScript from "@/components/MetaScript";

export function SettingsHelpPage() {
  const [content, setContent] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [rateLimitInfo, setRateLimitInfo] = useState<{
    rate_limit_reached: boolean;
    remaining_tickets: number;
    total_limit: number;
    period_hours: number;
    reset_time: string | null;
    recent_tickets_count: number;
  } | null>(null);
  const [isLoadingRateLimit, setIsLoadingRateLimit] = useState(true);
  const { handleSuccess, handleError } = useToastHandler();

  useEffect(() => {
    const checkRateLimit = async () => {
      try {
        const rateLimitData = await settingsApi.checkTicketRateLimit();
        setRateLimitInfo(rateLimitData);
      } catch (err) {
        console.error("Failed to check rate limit:", err);
        // If rate limit check fails, allow form to show (fail open)
        setRateLimitInfo({
          rate_limit_reached: false,
          remaining_tickets: 2,
          total_limit: 2,
          period_hours: 12,
          reset_time: null,
          recent_tickets_count: 0,
        });
      } finally {
        setIsLoadingRateLimit(false);
      }
    };

    checkRateLimit();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!content.trim()) {
      handleError("Please enter your content");
      return;
    }

    if (content.length > 1000) {
      handleError("Content must be less than 1000 characters");
      return;
    }

    try {
      setIsSubmitting(true);

      await settingsApi.submitTicket({
        content: content.trim(),
      });

      setIsSubmitted(true);
      handleSuccess(
        "Submitted successfully! We will get back to you soon.",
        3000
      );
      // redirect to settings page after 3 seconds
      setTimeout(() => {
        window.location.href = "/settings";
      }, 3000);

      // Refresh rate limit info after successful submission
      // try {
      //   const rateLimitData = await settingsApi.checkTicketRateLimit();
      //   setRateLimitInfo(rateLimitData);
      // } catch (refreshErr) {
      //   console.error("Failed to refresh rate limit info:", refreshErr);
      // }
    } catch (err: unknown) {
      console.error(err);
      handleError(err, 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col p-6">
      <PageTitleScript title="Help & Support" />
      <div className="flex items-center mb-6">
        <Link to="/settings" className="mr-2">
          <ArrowLeft size={24} />
        </Link>
        <h1 className="text-2xl font-bold text-tx-dark">Help & Support</h1>
      </div>

      <div className="rounded-lg shadow-md bg-white p-8 mb-4">
        <div className="flex items-center mb-4">
          <HelpCircle size={24} className="text-accent-bg-hot mr-3" />
          <h2 className="text-lg font-semibold text-tx-dark">
            How can we help you?
          </h2>
        </div>

        {isLoadingRateLimit ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-bg-hot"></div>
          </div>
        ) : rateLimitInfo?.rate_limit_reached ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
            <p className="text-gray-700 mb-4">
              You have reached the maximum limit of {rateLimitInfo.total_limit}{" "}
              support tickets every {rateLimitInfo.period_hours} hours.
            </p>
            {rateLimitInfo.reset_time && (
              <p className="flex items-center text-gray-700 mb-4">
                You can submit another ticket after{" "}
                {new Date(rateLimitInfo.reset_time).toLocaleString()}
              </p>
            )}
            <p className="text-gray-700">Thank you for your patience.</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-tx-default mb-6">
              Having trouble or have feedback? Send us a message and we'll get
              back to you as soon as possible.
              {/* {rateLimitInfo && rateLimitInfo.remaining_tickets > 0 && (
                <span className="block mt-2 text-xs text-amber-600">
                  You have {rateLimitInfo.remaining_tickets} message
                  {rateLimitInfo.remaining_tickets > 1 ? "s" : ""} remaining in
                  the next {rateLimitInfo.period_hours} hours.
                </span>
              )} */}
            </p>

            <form onSubmit={handleSubmit}>
              <div className="mb-6">
                <label
                  htmlFor="content"
                  className="block text-sm font-medium mb-2 text-tx-dark"
                >
                  Your Question or Feedback
                </label>
                <textarea
                  id="content"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Please describe your issue or feedback in detail..."
                  maxLength={1000}
                  rows={6}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-bg-hot focus:border-transparent resize-none"
                  disabled={isSubmitting || isSubmitted}
                />
                <div className="flex justify-between mt-2">
                  <span className="text-xs text-tx-default">
                    Maximum 1000 characters
                  </span>
                  <span className="text-xs text-tx-default">
                    {content.length}/1000
                  </span>
                </div>
              </div>

              <Button
                type="submit"
                variant="primary"
                size="lg"
                disabled={isSubmitting || !content.trim() || isSubmitted}
                className="w-full"
              >
                {isSubmitted ? (
                  <div className="flex items-center justify-center">
                    <Check size={20} className="mr-2" />
                    Message Sent Successfully
                  </div>
                ) : isSubmitting ? (
                  "Submitting..."
                ) : (
                  "Send Message"
                )}
              </Button>
            </form>
          </>
        )}
      </div>

      <div className="bg-gray-50 rounded-lg p-4">
        <p className="text-xs text-tx-default text-center">
          You can submit up to 2 messages every 12 hours.
        </p>
      </div>
    </div>
  );
}
